import * as React from 'react';
import { Box, Typography } from '@mui/material';
import { withStyles } from '../../settings/styles';
import { connect } from 'react-redux';
import { AppState } from '../../model/reducers';
import { ThunkDispatch } from 'redux-thunk';
import { loadingOverlay, broadcastMessage } from '../../model/overlay/actions';
import { BroadcastType } from '../../model/overlay/types';
import { UserState } from '../../model/user/types';
import { addUser, removeUser, editUser, AddUserType, EditUserType, RemoveUserType } from '../../model/user/actions';
import { NavigationTabProps } from '../../model/navigation/types';
import { UserIcons } from '../../settings/icons';
import SelectableItemList from '../../components/display/SelectableItemList';
import { UserSettings } from '../../settings/users';
import { PropertyType } from '../../types/PropertyType';
import CreateEditDialog from '../../components/dialogs/CreateEditDialog';
import { vsprintf } from 'sprintf-js';
import DeleteDialog from '../../components/dialogs/DeleteDialog';

// -----------------------------------------------  //
// ---------------- State & Props ----------------  //
// -----------------------------------------------  //

interface State 
{
    createEditDialog: {
        open: boolean;
        handle: number;
    }
    deleteDialog: {
        open: boolean,
        handle: number;
    }
};

interface StateProps
{
    users: UserState[];
}

interface DispatchProps
{
    loading: ()=>void;
    doneLoading: ()=>void;
    broadcast: (msg: string, type?: BroadcastType) => void;
    create: AddUserType;
    edit: EditUserType;
    remove: RemoveUserType;
}

type Props = NavigationTabProps & StateProps & DispatchProps;

// -----------------------------------------------  //
// -------------------- Class --------------------  //
// -----------------------------------------------  //

class InactiveUsers extends React.Component<Props, State> 
{
    constructor(props: any)
    {
        super(props);

        // Init State
        this.state = {
            createEditDialog: {
                open: false,
                handle: -1
            },
            deleteDialog: {
                open: false,
                handle: -1
            }
        };

        // Setup Member Functions
        this.renderAllUsers = this.renderAllUsers.bind(this);
        this.renderDialogs = this.renderDialogs.bind(this);
        this.dialogCreateEditOpen = this.dialogCreateEditOpen.bind(this);
        this.dialogCreateEditClose = this.dialogCreateEditClose.bind(this);
        this.dialogDeleteOpen = this.dialogDeleteOpen.bind(this);
        this.dialogDeleteClose = this.dialogDeleteClose.bind(this);

        this.actionCreate = this.actionCreate.bind(this);
        this.actionEdit = this.actionEdit.bind(this);
        this.actionDelete = this.actionDelete.bind(this);
    }

    // ============================================ //
    //               Render Functions               //
    // ============================================ //

    render () 
    {
        return (
            <Box sx={{width: "100%", height: "100%"}}>
                {this.renderTitle()}
                {this.renderAllUsers()}
                {this.renderDialogs()}
            </Box>
        );
    }

    renderTitle()
    {
        const { t } = this.props;
        return (<>
            <Box sx={{ maxWidth: 936, margin: 'auto', overflow: 'hidden' }}>
                <Typography sx={{marginBottom: "30px", fontSize: 18, fontWeight: 5}}>
                    {t('users.inactiveUsers')}
                </Typography>
            </Box>
        </>);
    }

    renderAllUsers()
    {
        const { t } = this.props;
        /* TODO
        const userList = Object.values(this.props.users);
        const usersAsListItems = userList.map(user => ({
            title: user.username,
            icon: getUserIcon(user.type),
            typeTitle: user.type
        }));
        */

        return (<SelectableItemList 
            typeName={t('users.user')}
            typeNamePlural={`${t('users.inactiveUsers')}`}
            items={[]}
            onItemClick={this.dialogCreateEditOpen}
        />);
    }

    renderDialogs()
    {
        return (<>
            {this.renderCreateDialog()}
            {this.renderEditDialog()}
            {this.renderDeleteDialog()}
        </>)
    }

    renderCreateDialog()
    {
        if(this.state.createEditDialog.handle >= 0) return;
        const { t } = this.props;
        const userSettings = Object.values(UserSettings.allSettings);
        const createData = {
            id: "createUser",
            title: t('users.user'),
            icon: UserIcons.GROUP,
            properties: [
                {
                    id: "name",
                    title: t('general.name'),
                    type: PropertyType.NAME
                },
                {
                    id: "password",
                    title: t('general.password'),
                    type: PropertyType.PASSWORD,
                    secret: true
                },
                {
                    id: "user",
                    title: t('users.users'),
                    objects: userSettings.map(setting=>({
                        id: setting.id,
                        title: t(setting.title),
                        icon: setting.icon,
                        properties: setting.properties
                    } as IObject))
                } as IObjects
            ]
        } as IObject;

        return (<CreateEditDialog 
            open={this.state.createEditDialog.open} 
            object={createData}
            onClose={this.dialogCreateEditClose} 
            onCreate={this.actionCreate}
        />);
    }

    renderEditDialog()
    {
        if(this.state.createEditDialog.handle < 0) return;
        const { t } = this.props;
        const editUser = this.props.users[this.state.createEditDialog.handle];
        const editSettings = UserSettings.getSettings(editUser.type);

        const createEditData = {
            id: editUser.account,
            title: editUser.username,
            icon: editSettings.icon,
            properties: [
                {
                    id: "name",
                    title: t('general.name'),
                    type: PropertyType.NAME,
                    value: editUser.username
                } as IProperty,
                {
                    id: "password",
                    title: t('general.password'),
                    type: PropertyType.PASSWORD,
                    secret: true
                },
                {
                    id: "user",
                    title: t('users.users'),
                    objects: [{
                        id: editSettings.id,
                        title: t(editSettings.title),
                        icon: editSettings.icon,
                        properties: editSettings.properties
                    }]
                } as IObjects
            ]
        } as IObject;

        return (<CreateEditDialog 
            open={this.state.createEditDialog.open} 
            object={createEditData}
            editing={true}
            onClose={this.dialogCreateEditClose}
            onEdit={this.actionEdit}
            onDelete={this.dialogDeleteOpen}
        />);
    }

    renderDeleteDialog()
    {
        if(this.state.deleteDialog.handle < 0) return;
        const { t } = this.props;
        const user = this.props.users[this.state.deleteDialog.handle];
        const settings = UserSettings.getSettings(user.type);

        return (<DeleteDialog
            open={this.state.deleteDialog.open}
            typeTitle={t(settings?.title)}
            itemTitle={user.username}
            onClose={this.dialogDeleteClose}
            onDelete={this.actionDelete}
        />)
    }

    // ============================================ //
    //               Action Functions               //
    // ============================================ //

    actionCreate(jsonData: any)
    {
        console.log(jsonData);
        const { t } = this.props;
        const { name, password, user } = jsonData.properties;

        this.dialogCreateEditClose();
        this.props.loading();
        this.props.create(
            name, 
            password,
            user.id
        ).then((state: UserState) => {
            this.props.doneLoading();
            this.props.broadcast(vsprintf(t('users.added'), [state.username]), BroadcastType.SUCCESS);
        }).catch((err) => {
            this.props.doneLoading();
            this.props.broadcast(vsprintf(t('users.addFailed'), [name]), BroadcastType.ERROR);
        });
    }

    actionEdit(jsonData: any)
    {
        console.log(jsonData);
        const { t } = this.props;
        const user = this.props.users[this.state.createEditDialog.handle];
        const id = jsonData.id;
        const { name, password } = jsonData.properties;

        this.dialogCreateEditClose();
        this.props.loading();
        this.props.edit(
            user.id,
            user.account,
            name, 
            password
        ).then((state: UserState) => {
            this.props.doneLoading();
            this.props.broadcast(vsprintf(t('users.edited'), [state.username]), BroadcastType.SUCCESS);
        }).catch(()=>{
            this.props.doneLoading();
            this.props.broadcast(vsprintf(t('users.editFailed'), [name]), BroadcastType.ERROR);
        });
    }

    actionDelete()
    {
        const { t } = this.props;
        const user = this.props.users[this.state.deleteDialog.handle];
        if(user === undefined) return;
        
        this.dialogDeleteClose();
        this.dialogCreateEditClose();
        this.props.loading();
        this.props.remove(
            user.id,
            user.account
        ).then(() => {
            this.props.doneLoading();
            this.props.broadcast(vsprintf(t('users.removed'), [user.username]), BroadcastType.SUCCESS);
        }).catch(()=>{
            this.props.doneLoading();
            this.props.broadcast(vsprintf(t('users.removeFailed'), [user.username]), BroadcastType.ERROR);
        });
    }

    // ============================================ //
    //               Dialog Functions               //
    // ============================================ //

    dialogCreateEditOpen(handle: number = -1)
    {
        this.setState({ createEditDialog: { open: true, handle: handle } });
    }

    dialogCreateEditClose()
    {
        this.setState({ createEditDialog: { open: false, handle: -1 } });
    }

    dialogDeleteOpen(id: string)
    {
        this.setState({ deleteDialog: { open: true, handle: this.props.users.findIndex(s=>s.id===id) } });
    }

    dialogDeleteClose()
    {
        this.setState({ deleteDialog: { open: false, handle: -1 } });
    }
}

// ----------------- Export -----------------  //

const mapStateToProps = (state: AppState, props: NavigationTabProps): StateProps => ({
    users: Object.values(state.user.registeredUsers)
});

const mapDispatchToProps = (dispatch: ThunkDispatch<{},{},any>, props: NavigationTabProps): DispatchProps => ({
    loading: ()=>dispatch(loadingOverlay(true)),
    doneLoading: ()=>dispatch(loadingOverlay(false)),
    broadcast: (msg, type?)=>dispatch(broadcastMessage(msg, type)),
    create: (username: string, password: string, type: string)=>addUser(username, password, type)(dispatch),
    edit: (id: string, account: string, username: string, password: string)=>editUser(id, account, username, password)(dispatch),
    remove: (id: string, account: string)=>removeUser(id, account)(dispatch),
});

export default withStyles(connect(mapStateToProps, mapDispatchToProps)(InactiveUsers));

// ------------------------------------------  //